import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, timeAgo, label } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/shared";
import {
  Users, Building2, Briefcase, Send, CalendarClock, FileText, Award, Kanban,
  TrendingUp, DollarSign, Activity as ActivityIcon,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie,
} from "recharts";

const KPI_META = [
  { key: "active_clients", label: "Active Clients", icon: Building2, to: "/clients" },
  { key: "active_jobs", label: "Active Jobs", icon: Briefcase, to: "/jobs" },
  { key: "total_candidates", label: "Candidates", icon: Users, to: "/candidates" },
  { key: "total_pipeline", label: "In Pipeline", icon: Kanban, to: "/pipeline" },
  { key: "submissions", label: "Submissions", icon: Send, to: "/submissions" },
  { key: "interviews", label: "Interviews", icon: CalendarClock, to: "/interviews" },
  { key: "offers", label: "Offers", icon: FileText, to: "/offers" },
  { key: "placements", label: "Placements", icon: Award, to: "/placements" },
];

const CHART_COLORS = ["#255058", "#d9a441", "#2f9e5f", "#3b7fd4", "#8b5cf6", "#c65a63"];

function KpiCard({ meta, value, onClick }) {
  const Icon = meta.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`command-center-kpi-${meta.key.replace(/_/g, "-")}`}
      className="text-left group"
    >
      <Card className="p-4 hover:border-[#255058]/40 hover:shadow-md transition-all h-full">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">{meta.label}</span>
          <Icon className="h-4 w-4 text-[#255058]/70 group-hover:text-[#255058]" />
        </div>
        <div className="mt-2 font-display text-2xl font-bold tracking-tight">{value ?? 0}</div>
      </Card>
    </button>
  );
}

function ConvBar({ label: l, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{l}</span>
        <span className="font-semibold">{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-[#255058]" style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

export default function CommandCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/command-center").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div>
        <PageHeader title="Command Center" subtitle="Loading your workspace…" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  const conv = data.conversions || {};
  const regionData = (data.regional_performance || []).map((r) => ({ name: r.region, revenue: r.revenue, placements: r.placements }));
  const stageData = (data.pipeline_by_stage || []).map((s) => ({ name: label(s.stage), count: s.count }));

  return (
    <div className="space-y-6">
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0]}`} subtitle="Your executive command center — every metric is live from the database." />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {KPI_META.map((m) => (
          <KpiCard key={m.key} meta={m} value={data.kpis?.[m.key]} onClick={() => navigate(m.to)} />
        ))}
      </div>

      {/* Revenue + conversions */}
      <div className="grid lg:grid-cols-3 gap-4">
        {data.revenue && (
          <Card className="p-5 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4"><DollarSign className="h-4 w-4 text-[#255058]" /><span className="font-display font-semibold">Revenue</span></div>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground">Total Placement Revenue</div>
                <div className="font-display text-2xl font-bold">{money(data.revenue.total_placement_revenue)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div><div className="text-xs text-muted-foreground">Collected</div><div className="font-semibold text-[#2f9e5f]">{money(data.revenue.collected)}</div></div>
                <div><div className="text-xs text-muted-foreground">Outstanding</div><div className="font-semibold text-[#c65a63]">{money(data.revenue.outstanding)}</div></div>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-[#255058]" /><span className="font-display font-semibold">Conversion Funnel</span></div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
            <ConvBar label="Fill Rate" value={conv.fill_rate || 0} />
            <ConvBar label="Submission → Interview" value={conv.submission_to_interview || 0} />
            <ConvBar label="Interview → Offer" value={conv.interview_to_offer || 0} />
            <ConvBar label="Offer → Placement" value={conv.offer_to_placement || 0} />
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Pipeline by Stage</div>
          {stageData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stageData} margin={{ left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {stageData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground py-10 text-center">No pipeline data</div>}
        </Card>

        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Revenue by Region</div>
          {regionData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={regionData} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {regionData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground py-10 text-center">No revenue data</div>}
        </Card>
      </div>

      {/* Leaderboard + activity */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Recruiter Leaderboard</div>
          <div className="space-y-2">
            {(data.leaderboard || []).map((r, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <span className="h-6 w-6 rounded-full bg-accent text-xs grid place-items-center font-semibold">{i + 1}</span>
                <span className="flex-1 text-sm font-medium truncate">{r.recruiter}</span>
                <span className="text-sm text-muted-foreground">{r.submissions} subs</span>
              </div>
            ))}
            {!data.leaderboard?.length && <div className="text-sm text-muted-foreground">No activity yet</div>}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4"><ActivityIcon className="h-4 w-4 text-[#255058]" /><span className="font-display font-semibold">Recent Activity</span></div>
          <div className="space-y-3">
            {(data.recent_activity || []).slice(0, 8).map((a) => (
              <div key={a.id} className="flex gap-3 text-sm">
                <div className="h-1.5 w-1.5 rounded-full bg-[#255058] mt-1.5 shrink-0" />
                <div className="min-w-0">
                  <div className="truncate">{a.summary}</div>
                  <div className="text-[11px] text-muted-foreground">{timeAgo(a.created_at)}</div>
                </div>
              </div>
            ))}
            {!data.recent_activity?.length && <div className="text-sm text-muted-foreground">No recent activity</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
